/**
 * A reviewer's own keys and marks for Review (the notes of 2026-09-25, Holger's
 * first feedback round): how the mode is switched, whether the pins show, and
 * how Kommentieren announces itself. One module, because three readers need
 * the same rule - the shell (its own keyboard), the SDK (the application's
 * keyboard and pointer) and the server (what may be stored) - and two copies
 * of a key rule drift.
 *
 * Pure, no DOM types: an event is read through the few fields it carries.
 */

import { isColor, type Color } from "./still-marks.ts";

/** What a keyboard or pointer event carries that the rules below read. */
export interface KeyState {
  key?: string;
  code?: string;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  metaKey: boolean;
}

export const MODIFIERS = ["Ctrl", "Shift", "Alt", "Meta"] as const;
export type Modifier = (typeof MODIFIERS)[number];

/** A key as a person names it: `Space`, `K`, `F2`, `Enter`. */
const KEY = /^(Space|Enter|Tab|Escape|Backspace|F([1-9]|1[0-2])|[A-Z0-9])$/;

/**
 * `Ctrl+Space`, `Shift+Alt+K`: modifiers in the fixed order Ctrl, Shift, Alt,
 * Meta, then one key. Null when it is not one, or has no modifier - a bare key
 * would fire while somebody types in the reviewed application.
 */
export function parseCombo(raw: unknown): { mods: Modifier[]; key: string } | null {
  if (typeof raw !== "string" || raw.length > 40) return null;
  const parts = raw.split("+").map((p) => p.trim());
  const key = parts.pop() ?? "";
  if (!KEY.test(key)) return null;
  const mods = parts as Modifier[];
  if (mods.length === 0 || mods.some((m) => !MODIFIERS.includes(m))) return null;
  if (new Set(mods).size !== mods.length) return null;
  return { mods: MODIFIERS.filter((m) => mods.includes(m)), key };
}

/** The same combination, written the one way it is stored. */
export function normalCombo(raw: unknown): string | undefined {
  const c = parseCombo(raw);
  return c ? [...c.mods, c.key].join("+") : undefined;
}

const held = (e: KeyState, m: Modifier): boolean =>
  m === "Ctrl" ? e.ctrlKey : m === "Shift" ? e.shiftKey : m === "Alt" ? e.altKey : e.metaKey;

/** The key an event names, in the vocabulary above - by `code`, so Shift or a layout does not change it. */
function keyOf(e: KeyState): string {
  const code = e.code ?? "";
  if (code === "Space") return "Space";
  if (/^Key[A-Z]$/.test(code)) return code.slice(3);
  if (/^Digit[0-9]$/.test(code)) return code.slice(5);
  if (/^F([1-9]|1[0-2])$/.test(code)) return code;
  if (code === "Enter" || code === "Tab" || code === "Escape" || code === "Backspace") return code;
  return (e.key ?? "").length === 1 ? (e.key ?? "").toUpperCase() : (e.key ?? "");
}

/** Whether a keydown is this combination - exactly its modifiers, no more. */
export function matchesCombo(e: KeyState, combo: string): boolean {
  const c = parseCombo(combo);
  if (!c) return false;
  return keyOf(e) === c.key && MODIFIERS.every((m) => held(e, m) === c.mods.includes(m));
}

/**
 * The mouse buttons a temporary mode switch may use, by `MouseEvent.button`:
 * 0 left, 1 middle, 2 right, 3 and 4 the side buttons most mice carry. Any of
 * them, because the reviewed application may already use the left, right or
 * middle click for something of its own (the notes of 2026-09-28).
 */
export const MOUSE_BUTTONS = ["LMB", "MMB", "RMB", "MB4", "MB5"] as const;
export type MouseButton = (typeof MOUSE_BUTTONS)[number];

/** How the Einstellungen page names a button. */
export const MOUSE_BUTTON_LABELS: Record<MouseButton, string> = {
  LMB: "Linksklick",
  MMB: "Mittelklick",
  RMB: "Rechtsklick",
  MB4: "Maustaste 4",
  MB5: "Maustaste 5",
};

/**
 * The temporary mode switch (*Temporärer Modus Wechsel*, the notes of
 * 2026-09-28): a click that is taken in the OTHER mode - `Ctrl+LMB`, `MB4`,
 * `Shift+Alt+RMB`. Modifiers in the fixed order, then one mouse button; no
 * modifier is allowed, because a side button needs none.
 *
 * Until 2026-09-28 it was a held modifier set (`Shift+Alt`) and the left click
 * was the one it changed. A value stored in that form still reads - as the
 * same set with the left button, which is what it did.
 */
export function normalSwitch(raw: unknown): string | undefined {
  if (typeof raw !== "string" || raw.length > 40) return undefined;
  const parts = raw.split("+").map((p) => p.trim());
  const last = parts[parts.length - 1] ?? "";
  const button = MOUSE_BUTTONS.includes(last as MouseButton) ? (parts.pop() as MouseButton) : null;
  const mods = parts.filter(Boolean) as Modifier[];
  if (mods.length !== parts.length) return undefined;
  if (mods.some((m) => !MODIFIERS.includes(m)) || new Set(mods).size !== mods.length) return undefined;
  if (button === null && mods.length < 2) return undefined; // the old form never took fewer than two
  return [...MODIFIERS.filter((m) => mods.includes(m)), button ?? "LMB"].join("+");
}

/** A switch, split: its modifiers and its button. */
export function switchParts(sw: string | null | undefined): { mods: Modifier[]; button: MouseButton } | null {
  const n = sw ? normalSwitch(sw) : undefined;
  if (!n) return null;
  const parts = n.split("+");
  return { mods: parts.slice(0, -1) as Modifier[], button: parts[parts.length - 1] as MouseButton };
}

/** Which button a pointer event is, in the vocabulary above; null for one we do not name. */
export const buttonOf = (button: number | undefined): MouseButton | null => MOUSE_BUTTONS[button ?? -1] ?? null;

/** Whether exactly these modifiers are down - no more. */
const exactly = (e: KeyState, mods: Modifier[]): boolean => MODIFIERS.every((m) => held(e, m) === mods.includes(m));

/** Whether a pointer event is the switch - exactly its modifiers and its button. */
export function switchClick(e: KeyState & { button?: number }, sw: string | null): boolean {
  const p = switchParts(sw);
  return p !== null && buttonOf(e.button) === p.button && exactly(e, p.mods);
}

/**
 * Whether the switch's modifiers are down right now - read from any event - so
 * the surface can show the other mode before the click. A switch without a
 * modifier has nothing to hold, and shows nothing ahead.
 */
export function switchHeld(e: KeyState, sw: string | null): boolean {
  const p = switchParts(sw);
  return p !== null && p.mods.length > 0 && exactly(e, p.mods);
}

/**
 * The system a reviewer works on, as far as its keys go (D-052): macOS takes
 * Ctrl + click and Ctrl + Space for itself, and Meta there is Cmd rather than
 * the Windows key. `mac` covers iPadOS too, which shares those keys. A system
 * named neither way - Linux, or one the browser does not name - is `other` and
 * keeps the Windows warnings it always got: each of them names its system.
 */
export type Platform = "mac" | "windows" | "other";

/**
 * The platform from the browser's own name for it: `navigator.userAgentData
 * .platform` (`macOS`, `Windows`) where there is one, else `navigator.platform`
 * (`MacIntel`, `Win32`, `iPad`). Takes the string rather than the navigator,
 * so the warnings below are tested without a browser.
 */
export function platformOf(raw: string | null | undefined): Platform {
  const p = (raw ?? "").toLowerCase();
  if (/^(mac|iphone|ipad|ipod|ios)/.test(p)) return "mac";
  if (p.startsWith("win")) return "windows";
  return "other";
}

/**
 * What a chosen switch costs, said before it bites (the notes of 2026-09-28:
 * *"different warnings for combinations"*), on the system it is chosen on
 * (D-052). Pure, so the page and a test read the same list.
 */
export function switchWarnings(sw: string | null, platform: Platform): string[] {
  const p = switchParts(sw);
  if (!p) return [];
  const has = (m: Modifier) => p.mods.includes(m);
  const bare = p.mods.length === 0;
  const mac = platform === "mac";
  const out: string[] = [];
  if (bare && p.button === "LMB")
    out.push("Ohne Zusatztaste wechselt jeder Linksklick den Modus: Im Browsen legt dann jeder Klick einen Eintrag an. „Nur mit Alt“ beim Öffnen eines Pins ist damit abgeschaltet.");
  if (bare && p.button === "RMB") out.push("Ohne Zusatztaste erreichen Sie das Kontextmenü der Anwendung nicht mehr.");
  if (bare && p.button === "MMB") out.push("Ohne Zusatztaste öffnet ein Mittelklick in der Anwendung keinen Link mehr in einem neuen Tab.");
  if (bare && (p.button === "MB4" || p.button === "MB5"))
    out.push("Die Maustasten 4 und 5 sind im Browser sonst Zurück und Vorwärts - über der Anwendung übernimmt sie Loupe.");
  // macOS reads Ctrl + click as the secondary click - the default switch among them.
  if (mac && has("Ctrl") && p.button === "LMB") out.push("Unter macOS gilt Strg + Linksklick als Rechtsklick und öffnet das Kontextmenü.");
  if (!mac && has("Shift") && has("Alt")) out.push("Unter Windows wechselt Alt + Umschalt auch die Tastaturbelegung, wenn mehrere eingerichtet sind.");
  if (!mac && has("Ctrl") && has("Shift")) out.push("Strg + Umschalt wechselt unter Windows je nach Einrichtung ebenfalls die Tastaturbelegung.");
  if (has("Shift")) out.push("Umschalt + Klick erweitert in vielen Anwendungen eine Text- oder Listenauswahl - diesen Klick nimmt dann Loupe.");
  if (has("Alt") && p.button === "LMB") out.push("Alt + Linksklick öffnet mit „nur mit Alt“ einen Pin - auf einem Pin gewinnt der Pin.");
  // Cmd opens no menu when let go; what it costs is the left click it changes.
  if (mac && has("Meta") && p.button === "LMB")
    out.push("Meta ist am Mac die Cmd-Taste: Cmd + Linksklick öffnet einen Link in einem neuen Tab und wählt in vielen Anwendungen einzelne Listeneinträge dazu - diesen Klick nimmt dann Loupe.");
  if (!mac && has("Meta")) out.push("Die Windows-Taste öffnet beim Loslassen das Startmenü.");
  return out;
}

/**
 * What the chosen *Modus wechseln* key costs (D-052) - the keyboard's side of
 * `switchWarnings`. Only what the system and the browser do with a
 * combination by default; what the reviewed application does with it, Loupe
 * cannot know.
 */
export function toggleWarnings(combo: string, platform: Platform): string[] {
  const c = parseCombo(combo);
  if (!c) return [];
  const exact = (...mods: Modifier[]) => c.mods.length === mods.length && mods.every((m) => c.mods.includes(m));
  const space = c.key === "Space";
  const out: string[] = [];
  if (platform === "mac") {
    if (space && exact("Ctrl")) out.push("Unter macOS wechselt Strg + Leertaste die Eingabequelle, wenn mehrere eingerichtet sind.");
    if (space && exact("Ctrl", "Alt")) out.push("Unter macOS wählt Strg + Alt + Leertaste die nächste Eingabequelle, wenn mehrere eingerichtet sind.");
    if (c.mods.includes("Meta"))
      out.push(
        space && exact("Meta")
          ? "Meta ist am Mac die Cmd-Taste, und Cmd + Leertaste öffnet Spotlight."
          : space && exact("Alt", "Meta")
            ? "Meta ist am Mac die Cmd-Taste, und Cmd + Alt + Leertaste öffnet ein Finder-Suchfenster."
            : space && exact("Ctrl", "Meta")
              ? "Meta ist am Mac die Cmd-Taste, und Strg + Cmd + Leertaste öffnet „Emoji & Symbole“."
              : "Meta ist am Mac die Cmd-Taste, und die meisten Kombinationen mit Cmd sind schon vom Browser oder vom System belegt - Cmd + W etwa schließt den Tab, Cmd + Q beendet den Browser.",
      );
  } else if (c.mods.includes("Meta"))
    out.push("Kombinationen mit der Windows-Taste fängt Windows meist selbst ab - Windows + Leertaste etwa wechselt die Tastaturbelegung.");
  return out;
}

/** How Kommentieren announces itself around the reviewed application. */
export const INDICATORS = ["none", "bottom", "frame"] as const;
export type Indicator = (typeof INDICATORS)[number];

export const INDICATOR_WIDTH = { min: 1, max: 12 } as const;

/**
 * When a pin takes a click in Browsen (the notes of 2026-09-25): `always` - a
 * shown pin is the most natural thing to click, and it can be hidden since the
 * pins toggle - or only while Alt is held, for a reviewer who clicks through
 * the application more than into the entries.
 */
export const PIN_CLICKS = ["always", "alt"] as const;
export type PinClick = (typeof PIN_CLICKS)[number];
export const cleanPinClick = (v: unknown): PinClick | undefined => (PIN_CLICKS.includes(v as PinClick) ? (v as PinClick) : undefined);

/** What the SDK needs to know, in one message and one shape. */
export interface ReviewPrefs {
  /** Toggles Browsen / Kommentieren. */
  toggle: string;
  /** The temporary mode switch - a click taken in the other mode (`Ctrl+LMB`); null = off. */
  hold: string | null;
  pins: boolean;
  /** The pins' colour, `#rrggbb` or `#rrggbbaa` - see-through allowed (the notes of 2026-09-28). */
  pinColor: Color;
  /**
   * The shell's alone, carried here because it is read beside the rest: a click
   * on the Review tab opens the application at once (the notes of 2026-09-28).
   * The SDK ignores it.
   */
  autoOpen: boolean;
  /** When a pin opens its entry in Browsen. */
  pinClick: PinClick;
  indicator: Indicator;
  color: Color;
  width: number;
}

export const DEFAULT_PREFS: ReviewPrefs = {
  toggle: "Ctrl+Space",
  hold: "Ctrl+LMB",
  pins: true,
  /* The pin's red since V0.1, now the default of a choice. */
  pinColor: "#d43f3a",
  autoOpen: false,
  pinClick: "always",
  indicator: "none",
  color: "#e2736e",
  width: 3,
};

/** The stored settings as the prefs they mean, defaults filled in. */
export function prefsOf(s: {
  key_mode_toggle?: string;
  key_mode_hold?: string | false;
  review_pins?: boolean;
  review_pin_color?: Color;
  review_autoopen?: boolean;
  review_pin_click?: PinClick;
  review_indicator?: Indicator;
  review_indicator_color?: Color;
  review_indicator_width?: number;
}): ReviewPrefs {
  const hold = s.key_mode_hold === false ? null : (normalSwitch(s.key_mode_hold) ?? DEFAULT_PREFS.hold);
  return {
    toggle: s.key_mode_toggle ?? DEFAULT_PREFS.toggle,
    hold,
    pins: s.review_pins ?? DEFAULT_PREFS.pins,
    pinColor: s.review_pin_color ?? DEFAULT_PREFS.pinColor,
    autoOpen: s.review_autoopen ?? DEFAULT_PREFS.autoOpen,
    // A bare left click as the switch takes every click, so "only with Alt" has nothing left to mean.
    pinClick: hold === "LMB" ? "always" : (s.review_pin_click ?? DEFAULT_PREFS.pinClick),
    indicator: s.review_indicator ?? DEFAULT_PREFS.indicator,
    color: s.review_indicator_color ?? DEFAULT_PREFS.color,
    width: s.review_indicator_width ?? DEFAULT_PREFS.width,
  };
}

export const cleanIndicator = (v: unknown): Indicator | undefined => (INDICATORS.includes(v as Indicator) ? (v as Indicator) : undefined);
export const cleanIndicatorColor = (v: unknown): Color | undefined => (isColor(v) ? (v as string).toLowerCase() : undefined);
export function cleanIndicatorWidth(v: unknown): number | undefined {
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return undefined;
  return Math.min(INDICATOR_WIDTH.max, Math.max(INDICATOR_WIDTH.min, Math.round(n)));
}
